import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SERVER_PUBLIC_IP } from "../constants";
import {
  Box,
  Typography,
  Button,
  Paper,
  Skeleton,
  Alert,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import StopIcon from "@mui/icons-material/Stop";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MemoryIcon from "@mui/icons-material/Memory";
import NetworkCheckIcon from "@mui/icons-material/NetworkCheck";

import {
  useAppDispatch,
  useAppSelector,
  fetchInstanceByInstanceId,
  terminateInstance,
  stopInstance,
  startInstance,
  restartInstance,
  fetchInstanceLogs,
  clearLogs,
  showToast,
} from "../store";
import { StatusBadge, CountdownTimer } from "../components/instances";
import { ServiceIcon } from "../components/common";
import CredentialsDisplay from "../components/instances/CredentialsDisplay";
import { formatDate, formatDateTime } from "../utils/formatters";
import { instanceService } from "../services";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const InstanceDetailPage: React.FC = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    selectedInstance,
    isLoading,
    isTerminating,
    isStopping,
    isStarting,
    isRestarting,
    logs,
    isLoadingLogs,
    error,
  } = useAppSelector((state) => state.instances);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<
    "terminate" | "stop" | "restart"
  >("terminate");
  const [tabValue, setTabValue] = useState(0);
  const [composeYaml, setComposeYaml] = useState<string | null>(null);
  const [composeLoading, setComposeLoading] = useState(false);

  const handleFetchCompose = async () => {
    if (composeYaml || composeLoading || !instanceId) return;
    setComposeLoading(true);
    try {
      const res = await instanceService.getCompose(instanceId);
      setComposeYaml(res.yaml);
    } catch {
      setComposeYaml("# Failed to load compose file");
    } finally {
      setComposeLoading(false);
    }
  };

  useEffect(() => {
    if (instanceId) {
      dispatch(fetchInstanceByInstanceId(instanceId));
    }
    return () => {
      dispatch(clearLogs());
    };
  }, [instanceId, dispatch]);

  const handleBack = () => {
    navigate("/instances");
  };

  const handleConfirmAction = (action: "terminate" | "stop" | "restart") => {
    setConfirmAction(action);
    setConfirmOpen(true);
  };

  const handleExecuteAction = async () => {
    if (!selectedInstance) return;

    try {
      switch (confirmAction) {
        case "terminate":
          await dispatch(
            terminateInstance(selectedInstance.instanceId),
          ).unwrap();
          dispatch(
            showToast({
              message: "Instance terminated successfully",
              severity: "success",
            }),
          );
          break;
        case "stop":
          await dispatch(stopInstance(selectedInstance.instanceId)).unwrap();
          dispatch(
            showToast({
              message: "Instance stopped successfully",
              severity: "success",
            }),
          );
          break;
        case "restart":
          await dispatch(restartInstance(selectedInstance.instanceId)).unwrap();
          dispatch(
            showToast({ message: "Instance restarting", severity: "success" }),
          );
          break;
      }
      setConfirmOpen(false);
    } catch {
      dispatch(
        showToast({
          message: `Failed to ${confirmAction} instance`,
          severity: "error",
        }),
      );
    }
  };

  const handleStart = async () => {
    if (!selectedInstance) return;
    try {
      await dispatch(startInstance(selectedInstance.instanceId)).unwrap();
      dispatch(
        showToast({
          message: "Instance started successfully",
          severity: "success",
        }),
      );
    } catch {
      dispatch(
        showToast({ message: "Failed to start instance", severity: "error" }),
      );
    }
  };

  const handleFetchLogs = () => {
    if (instanceId) {
      dispatch(fetchInstanceLogs({ instanceId }));
    }
  };

  const isRunning = selectedInstance?.status === "RUNNING";
  const isStopped = selectedInstance?.status === "STOPPED";
  const isActive =
    selectedInstance?.status === "RUNNING" ||
    selectedInstance?.status === "PROVISIONING" ||
    selectedInstance?.status === "STOPPED";

  const getConfirmDialogContent = () => {
    switch (confirmAction) {
      case "terminate":
        return {
          title: "Terminate Instance?",
          content:
            "Are you sure you want to terminate this instance? This action cannot be undone.",
          buttonText: isTerminating ? "Terminating..." : "Terminate",
        };
      case "stop":
        return {
          title: "Stop Instance?",
          content:
            "The expiration timer will be paused while stopped. You can restart it later.",
          buttonText: isStopping ? "Stopping..." : "Stop",
        };
      case "restart":
        return {
          title: "Restart Instance?",
          content: "The service will be briefly unavailable during restart.",
          buttonText: isRestarting ? "Restarting..." : "Restart",
        };
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton
          variant="rectangular"
          height={100}
          sx={{ borderRadius: 2, mb: 3 }}
        />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Instances
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!selectedInstance) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Instances
        </Button>
        <Alert severity="warning">Instance not found</Alert>
      </Box>
    );
  }

  const dialogContent = getConfirmDialogContent();
  const serviceName =
    selectedInstance.serviceName ||
    selectedInstance.service?.name ||
    "Unknown Service";

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      {/* Instance Overview Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <IconButton onClick={handleBack} size="small">
            <ArrowBackIcon />
          </IconButton>
          <ServiceIcon serviceName={serviceName} size={56} />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "text.primary",
              }}
            >
              {serviceName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontFamily: "monospace",
                fontSize: "0.8rem",
              }}
            >
              {selectedInstance.instanceId}
            </Typography>
          </Box>
          <StatusBadge status={selectedInstance.status} size="medium" />
        </Box>

        {/* Info Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
            gap: 2,
          }}
        >
          <Box sx={{ bgcolor: "#f9fafb", borderRadius: 2, p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: "#6b7280" }} />
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                Created
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "text.primary",
              }}
            >
              {formatDate(selectedInstance.createdAt)}
            </Typography>
          </Box>
          <Box sx={{ bgcolor: "#f9fafb", borderRadius: 2, p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: "#6b7280" }} />
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                Expires
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "text.primary",
              }}
            >
              {formatDate(selectedInstance.expiresAt)}
            </Typography>
          </Box>
          {selectedInstance.hostPort && (
            <Box sx={{ bgcolor: "#f9fafb", borderRadius: 2, p: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <NetworkCheckIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  Host:Port
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  fontFamily: "monospace",
                  fontSize: "0.8rem",
                }}
              >
                {selectedInstance.hostPort?.replace(
                  "0.0.0.0",
                  SERVER_PUBLIC_IP,
                )}
              </Typography>
            </Box>
          )}
          {selectedInstance.domainUrl && (
            <Box sx={{ bgcolor: "#f9fafb", borderRadius: 2, p: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <NetworkCheckIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  Domain
                </Typography>
              </Box>
              <Typography
                variant="body2"
                component="a"
                href={selectedInstance.domainUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontWeight: 600,
                  color: "primary.main",
                  fontFamily: "monospace",
                  fontSize: "0.8rem",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {selectedInstance.domainUrl}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
      <Box
        sx={{
          display: "flex",
          gap: 3,
          flexDirection: { xs: "column", lg: "row" },
        }}
      >
        {/* Left: Tabs content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Metrics Cards (shown for active instances) */}
          {isActive && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
                mb: 3,
              }}
            >
              <Paper
                sx={{
                  p: 3,
                  background: "linear-gradient(135deg, #eff6ff, #dbeafe)",
                  border: "1px solid #bfdbfe",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        textTransform: "uppercase",
                        color: "#1e40af",
                      }}
                    >
                      Status
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: "#1d4ed8",
                        mt: 1,
                      }}
                    >
                      {selectedInstance.status}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: "#bfdbfe",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <MemoryIcon sx={{ color: "#1d4ed8", fontSize: 24 }} />
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#1e40af",
                  }}
                >
                  Service: {serviceName}
                </Typography>
              </Paper>

              <Paper
                sx={{
                  p: 3,
                  background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
                  border: "1px solid #bbf7d0",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        textTransform: "uppercase",
                        color: "#166534",
                      }}
                    >
                      Time Remaining
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <CountdownTimer
                        expiresAt={selectedInstance.expiresAt}
                        showIcon={false}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: "#bbf7d0",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AccessTimeIcon sx={{ color: "#166534", fontSize: 24 }} />
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#166534",
                  }}
                >
                  {isStopped ? "Timer paused" : "Until expiration"}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Tabs */}
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={tabValue}
              onChange={(_, v) => setTabValue(v)}
              sx={{
                px: 1,
                pt: 1,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 500,
                  minHeight: 44,
                },
                "& .Mui-selected": { fontWeight: 600 },
              }}
            >
              <Tab label="Connection" />
              <Tab label="Logs" />
              <Tab label="Details" />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              <Box sx={{ p: 3 }}>
                {isActive ? (
                  <>
                    {selectedInstance.hostPort && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="body2"
                          gutterBottom
                          sx={{
                            color: "text.secondary",
                            fontWeight: 500,
                          }}
                        >
                          Host:Port
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: "monospace",
                            bgcolor: "#f3f4f6",
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                            display: "inline-block",
                          }}
                        >
                          {selectedInstance.hostPort?.replace(
                            "0.0.0.0",
                            SERVER_PUBLIC_IP,
                          )}
                        </Typography>
                      </Box>
                    )}
                    {selectedInstance.domainUrl && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="body2"
                          gutterBottom
                          sx={{
                            color: "text.secondary",
                            fontWeight: 500,
                          }}
                        >
                          Domain URL
                        </Typography>
                        <Typography
                          variant="body1"
                          component="a"
                          href={selectedInstance.domainUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontFamily: "monospace",
                            bgcolor: "#f3f4f6",
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                            display: "inline-block",
                            color: "primary.main",
                            textDecoration: "none",
                          }}
                        >
                          {selectedInstance.domainUrl}
                        </Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 2 }} />
                    <CredentialsDisplay
                      instanceId={selectedInstance.instanceId}
                      username={selectedInstance.username ?? undefined}
                      hasCredentials={selectedInstance.hasCredentials}
                    />
                    <Accordion
                      onChange={(_, expanded) => {
                        if (expanded) handleFetchCompose();
                      }}
                      sx={{
                        mt: 3,
                        boxShadow: "none",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "8px !important",
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                          }}
                        >
                          Docker Compose
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {composeLoading ? (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              py: 3,
                            }}
                          >
                            <CircularProgress size={24} />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              bgcolor: "#111827",
                              color: "#d1d5db",
                              p: 2,
                              borderRadius: 1,
                              fontFamily: "monospace",
                              fontSize: "0.85rem",
                              whiteSpace: "pre",
                              overflow: "auto",
                            }}
                          >
                            {composeYaml || "Expand to load compose file"}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </>
                ) : (
                  <Alert severity="info">
                    Connection details are only available for active instances.
                  </Alert>
                )}
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Box sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    Instance Logs
                  </Typography>
                  <Button
                    startIcon={
                      isLoadingLogs ? (
                        <CircularProgress size={16} />
                      ) : (
                        <RefreshIcon />
                      )
                    }
                    onClick={handleFetchLogs}
                    disabled={isLoadingLogs || !isActive}
                    size="small"
                    variant="outlined"
                  >
                    {isLoadingLogs ? "Loading..." : "Refresh"}
                  </Button>
                </Box>
                {logs ? (
                  <Box
                    sx={{
                      bgcolor: "#111827",
                      color: "#d1d5db",
                      p: 2,
                      borderRadius: 1,
                      maxHeight: 500,
                      overflow: "auto",
                      fontFamily: "monospace",
                      fontSize: "0.85rem",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                    }}
                  >
                    {logs.logs || "No logs available"}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography
                      variant="body2"
                      gutterBottom
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      Click refresh to fetch instance logs
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={handleFetchLogs}
                      disabled={!isActive}
                      sx={{ mt: 1 }}
                    >
                      Fetch Logs
                    </Button>
                  </Box>
                )}
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: "grid", gap: 2 }}>
                  {[
                    {
                      label: "Instance ID",
                      value: selectedInstance.instanceId,
                      mono: true,
                    },
                    { label: "Service", value: serviceName },
                    {
                      label: "Created",
                      value: formatDateTime(selectedInstance.createdAt),
                    },
                    {
                      label: "Expires",
                      value: formatDateTime(selectedInstance.expiresAt),
                    },
                    ...(selectedInstance.terminatedAt
                      ? [
                          {
                            label: "Terminated",
                            value: formatDateTime(
                              selectedInstance.terminatedAt,
                            ),
                          },
                        ]
                      : []),
                    ...(selectedInstance.errorMessage
                      ? [
                          {
                            label: "Error",
                            value: selectedInstance.errorMessage,
                          },
                        ]
                      : []),
                  ].map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        borderBottom: "1px solid #f3f4f6",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        color={
                          item.label === "Error" ? "error.main" : "text.primary"
                        }
                        sx={{
                          fontWeight: 500,

                          fontFamily: (item as { mono?: boolean }).mono
                            ? "monospace"
                            : undefined,

                          fontSize: (item as { mono?: boolean }).mono
                            ? "0.8rem"
                            : undefined,

                          textAlign: "right",
                          maxWidth: "60%",
                          wordBreak: "break-all",
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </TabPanel>
          </Paper>
        </Box>

        {/* Right: Quick Actions */}
        <Box sx={{ width: { xs: "100%", lg: 320 }, flexShrink: 0 }}>
          <Paper sx={{ p: 3, position: "sticky", top: 16 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
              }}
            >
              Quick Actions
            </Typography>

            {isActive ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.5,
                  mt: 2,
                }}
              >
                {isRunning && (
                  <>
                    <Button
                      variant="outlined"
                      onClick={() => handleConfirmAction("stop")}
                      disabled={isStopping}
                      sx={{
                        py: 2.5,
                        flexDirection: "column",
                        gap: 1,
                        bgcolor: "#fff7ed",
                        borderColor: "#fed7aa",
                        color: "#c2410c",
                        "&:hover": {
                          bgcolor: "#ffedd5",
                          borderColor: "#fdba74",
                        },
                      }}
                    >
                      <StopIcon />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        Stop
                      </Typography>
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => handleConfirmAction("restart")}
                      disabled={isRestarting}
                      sx={{
                        py: 2.5,
                        flexDirection: "column",
                        gap: 1,
                        bgcolor: "#eff6ff",
                        borderColor: "#bfdbfe",
                        color: "#1d4ed8",
                        "&:hover": {
                          bgcolor: "#dbeafe",
                          borderColor: "#93c5fd",
                        },
                      }}
                    >
                      <RestartAltIcon />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        Restart
                      </Typography>
                    </Button>
                  </>
                )}
                {isStopped && (
                  <Button
                    variant="outlined"
                    onClick={handleStart}
                    disabled={isStarting}
                    sx={{
                      py: 2.5,
                      flexDirection: "column",
                      gap: 1,
                      gridColumn: "span 2",
                      bgcolor: "#f0fdf4",
                      borderColor: "#bbf7d0",
                      color: "#166534",
                      "&:hover": { bgcolor: "#dcfce7", borderColor: "#86efac" },
                    }}
                  >
                    <PlayArrowIcon />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {isStarting ? "Starting..." : "Start"}
                    </Typography>
                  </Button>
                )}
                <Button
                  variant="outlined"
                  onClick={() => handleConfirmAction("terminate")}
                  disabled={isTerminating}
                  sx={{
                    py: 2.5,
                    flexDirection: "column",
                    gap: 1,
                    gridColumn: "span 2",
                    bgcolor: "#fef2f2",
                    borderColor: "#fecaca",
                    color: "#dc2626",
                    "&:hover": { bgcolor: "#fee2e2", borderColor: "#fca5a5" },
                  }}
                >
                  <DeleteIcon />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {isTerminating ? "Terminating..." : "Terminate"}
                  </Typography>
                </Button>
              </Box>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                This instance is no longer active.
              </Alert>
            )}

            {/* Time remaining section */}
            {isActive && (
              <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid #e5e7eb" }}>
                <Typography
                  variant="body2"
                  gutterBottom
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                  }}
                >
                  Expiration
                </Typography>
                <Box sx={{ textAlign: "center", py: 1 }}>
                  <CountdownTimer
                    expiresAt={selectedInstance.expiresAt}
                    showIcon={false}
                  />
                  {isStopped && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "warning.main",
                        mt: 0.5,
                        display: "block",
                      }}
                    >
                      Timer paused while stopped
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
      {/* Confirm Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{dialogContent.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogContent.content}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleExecuteAction}
            color={confirmAction === "terminate" ? "error" : "primary"}
            variant="contained"
            disabled={isTerminating || isStopping || isRestarting}
          >
            {dialogContent.buttonText}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstanceDetailPage;
